import { AssertNeverError } from '@omnea-digital/toolbox'
import { z } from 'zod'
import { ref, refOn } from '@omnea-app/packages/universal/references/refKinds'
import { RequestStepTaskTypeEnum } from '@omnea-app/packages/universal/tasks/taskEnums'
import { AgentConfidenceSchema } from './agentRunContracts'
import { SlicesConfigReadSchema, SlicesConfigSchema } from './sliceCatalogue'
import type { BlockTask } from '@omnea-app/packages/universal/workflow/taskTypes'

// A malformed value catches to whatever the field parses `undefined` to — its declared default, or
// undefined — so the fallback can never drift from the field's own definition. The load-time parse fails
// fast on a field that can't tolerate absence, which would otherwise make the catch itself throw mid-read.
const selfHealField = <Field extends z.ZodType>(field: Field) => {
  field.parse(undefined)
  return field.catch(() => field.parse(undefined))
}

// Narrower than `z.ZodRawShape` (whose values are the core `$ZodType`, which `.catch` does not exist on).
type CapabilityShape = Record<string, z.ZodType>

type SelfHealedShape<Shape extends CapabilityShape> = { [Key in keyof Shape]: z.ZodCatch<Shape[Key]> }

export const CustomAgentIdSchema = refOn(z.uuid(), 'customAgent')

const selfHealShape = <Shape extends CapabilityShape>(shape: Shape): SelfHealedShape<Shape> =>
  // Object.fromEntries erases the per-key schema types the mapped type preserves.
  Object.fromEntries(
    Object.entries(shape).map(([key, field]) => [key, selfHealField(field)])
  ) as unknown as SelfHealedShape<Shape>

// Self-healing keeps a capability usable when one field is corrupt, but a config that couldn't be read
// cleanly is not a safe basis for unattended action: a corrupt `instructions` reads as no instructions,
// which an intact autonomy block would still auto-apply. Dropping autonomy leaves the capability running
// and recommending, with a human applying the outcome.
const withAutonomySuppressed = <Config extends object>(config: Config): Config => ({ ...config, autonomy: undefined })

// Derives a capability's read artefacts from its read shape. The shape as written is the malformed-probe
// — a present field of the wrong type fails it, while missing fields and unknown-but-string slice names
// are legitimate (legacy/removed slices). The parse schema is the same shape with every field self-healed,
// so one malformed field never voids its siblings and `instructions` survives a corrupt `slices`.
const capabilityReadContract = <Shape extends CapabilityShape>(shape: Shape) => {
  const readProbe = z.object(shape)
  const readSchema = z.object(selfHealShape(shape))
  return {
    readSchema,
    parseForRead: (capabilityRaw: unknown): { config: z.infer<typeof readSchema> | undefined; malformed: boolean } => {
      const malformed = capabilityRaw != null && !readProbe.safeParse(capabilityRaw).success
      const config = readSchema.safeParse(capabilityRaw).data
      return { config: config !== undefined && malformed ? withAutonomySuppressed(config) : config, malformed }
    },
  }
}

// The review part of the agent config — `AgentConfigV2` nests exactly this shape under `review`.
export const AgenticReviewConfigSchema = z.object({
  enabled: z.boolean(),
  instructions: z.string(),
  customAgentId: CustomAgentIdSchema.optional(),
  slices: SlicesConfigSchema.optional().default({}),
  autonomy: z
    .object({
      approval: z.object({ enabled: z.boolean().default(false) }),
      rejection: z.object({ enabled: z.boolean().default(false) }),
      suggestedActions: z.object({ enabled: z.boolean().default(false) }).optional(),
    })
    .optional(),
  // Provenance for a config installed from a marketplace template; install is a copy, so nothing
  // branches on it and the org is free to edit the config afterwards.
  sourceTemplateId: z.uuid().optional(),
})
export type AgenticReviewConfig = z.infer<typeof AgenticReviewConfigSchema>

// Read side: every field optional, because persisted configs may predate any of them.
const reviewContract = capabilityReadContract({
  enabled: z.boolean().optional(),
  instructions: z.string().optional(),
  customAgentId: CustomAgentIdSchema.optional(),
  slices: SlicesConfigReadSchema.optional().default({}),
  // The legacy top-level policy list, read-side only. It stays string-only because uuid validation is
  // write-side and a bad id fails loud in `loadPolicies`.
  policyIds: z.array(z.string()).optional().default([]),
  // Tolerates a one-sided block (a missing `approval`/`rejection`) where the write schema requires both,
  // but stays strict on the `enabled` type so a malformed side trips the malformed probe and the whole
  // block catches to `undefined` — the gate then fails safe to human review rather than honouring half
  // of a corrupt block.
  autonomy: z
    .object({
      approval: z.object({ enabled: z.boolean().optional() }).optional(),
      rejection: z.object({ enabled: z.boolean().optional() }).optional(),
      suggestedActions: z.object({ enabled: z.boolean().optional() }).optional(),
    })
    .optional(),
})

// The original flat agent config stored this shape at the top level of `payload.agentConfig`, carrying
// the policy list top-level (V2 stores it only on the `slices.policies` entry). Persisted workflows
// still hold it; the UI only writes `AgentConfigV2`, so it survives read-side only.
export const LegacyAgentConfigSchema = z.object({
  ...AgenticReviewConfigSchema.omit({ customAgentId: true }).shape,
  policyIds: z.array(refOn(z.uuid(), 'policy')).optional(),
})
export type LegacyAgentConfig = z.infer<typeof LegacyAgentConfigSchema>

export const AgenticFillInConfigSchema = z.object({
  enabled: z.boolean(),
  instructions: z.string(),
  customAgentId: CustomAgentIdSchema.optional(),
  slices: SlicesConfigSchema.optional().default({}),
  // Autonomous completion: the agent marks the filled task complete rather than leaving it for a human.
  autonomy: z
    .object({
      enabled: z.boolean().default(false),
      minConfidence: AgentConfidenceSchema.optional(),
      allowUnreadableContext: z.boolean().optional(),
    })
    .optional(),
  // Provenance for a config installed from a marketplace template; install is a copy, so nothing
  // branches on it and the org is free to edit the config afterwards.
  sourceTemplateId: z.uuid().optional(),
})
export type AgenticFillInConfig = z.infer<typeof AgenticFillInConfigSchema>

const fillInContract = capabilityReadContract({
  enabled: z.boolean().optional(),
  instructions: z.string().optional(),
  customAgentId: CustomAgentIdSchema.optional(),
  slices: SlicesConfigReadSchema.optional().default({}),
  autonomy: z
    .object({
      enabled: z.boolean().optional(),
      minConfidence: AgentConfidenceSchema.optional(),
      allowUnreadableContext: z.boolean().optional(),
    })
    .optional(),
})

export const AgenticContentGenerationConfigSchema = z.object({
  enabled: z.boolean(),
  instructions: z.string(),
  customAgentId: CustomAgentIdSchema.optional(),
  slices: SlicesConfigSchema.optional().default({}),
  // Whether the rendered document carries the organisation's own logo in its page header. Optional
  // with no default so configs from before this field existed stay valid as literals; absent is off.
  includeLogo: z.boolean().optional(),
  // Autonomous completion: the agent completes the task with its drafted document rather than leaving the
  // draft for a human.
  autonomy: z.object({ enabled: z.boolean().default(false) }).optional(),
  // Provenance for a config installed from a marketplace template; install is a copy, so nothing
  // branches on it and the org is free to edit the config afterwards.
  sourceTemplateId: z.uuid().optional(),
})
export type AgenticContentGenerationConfig = z.infer<typeof AgenticContentGenerationConfigSchema>

const contentGenerationContract = capabilityReadContract({
  enabled: z.boolean().optional(),
  instructions: z.string().optional(),
  customAgentId: CustomAgentIdSchema.optional(),
  slices: SlicesConfigReadSchema.optional().default({}),
  includeLogo: z.boolean().optional().default(false),
  autonomy: z.object({ enabled: z.boolean().optional() }).optional(),
})

// Every capability an `AgentConfigV2` can carry. The classification probes (`isAgentConfigV2`,
// `isRawAgentConfigV2`, the schema refine), the V2 object shape (via `satisfies`) and the rollback
// disablers all key off this union, so a capability missing from any of them is a compile error.
export const AGENT_CAPABILITY_KEYS = ['review', 'fillIn', 'contentGeneration'] as const
export type AgentCapabilityKey = (typeof AGENT_CAPABILITY_KEYS)[number]

const checkSingleConfigSource = (
  config: { customAgentId?: string; sourceTemplateId?: string },
  context: z.RefinementCtx,
  path: PropertyKey[]
) => {
  if (config.customAgentId != null && config.sourceTemplateId != null) {
    context.addIssue({
      code: 'custom',
      message: 'An agent config cannot reference both a custom agent and a library template',
      path,
    })
  }
}

// Every part is optional so a config can carry only the sides it uses. The builder currently writes every
// side, with `enabled: false` for an unused one. At least one part must be present: an empty object would
// otherwise validate here yet be classified as legacy by `isAgentConfigV2`.
export const AgentConfigV2Schema = z
  .object({
    review: AgenticReviewConfigSchema.optional(),
    fillIn: AgenticFillInConfigSchema.optional(),
    contentGeneration: AgenticContentGenerationConfigSchema.optional(),
  } satisfies Record<AgentCapabilityKey, z.ZodType>)
  .refine((config) => AGENT_CAPABILITY_KEYS.some((key) => config[key] !== undefined), {
    message: 'At least one capability must be configured',
  })
  .superRefine((config, context) => {
    for (const capabilityKey of AGENT_CAPABILITY_KEYS) {
      const capability = config[capabilityKey]
      if (capability) checkSingleConfigSource(capability, context, [capabilityKey])
    }
  })

export type AgentConfigV2 = z.infer<typeof AgentConfigV2Schema>

// The template types a workflow block slot can be filled by: each names one capability the block runs.
// Slot code takes this rather than the full catalogue union, so a value with no capability key cannot
// reach it.
export const TaskAgentTemplateTypeSchema = z.enum(['FILL_IN', 'REVIEW', 'CONTENT_GENERATION'])
export type TaskAgentTemplateType = z.infer<typeof TaskAgentTemplateTypeSchema>

export const CUSTOM_AGENT_NAME_MAX_LENGTH = 100
export const DEFAULT_CUSTOM_AGENT_ALLOWANCE = 2

// The organisation setting as stored: a slot count, or unlimited.
export const StoredCustomAgentAllowanceSchema = z.union([
  z.number().int().nonnegative(),
  z.strictObject({ type: z.literal('unlimited') }),
])
export type StoredCustomAgentAllowance = z.infer<typeof StoredCustomAgentAllowanceSchema>

export const CustomAgentIdentitySchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(CUSTOM_AGENT_NAME_MAX_LENGTH),
  type: TaskAgentTemplateTypeSchema,
})
export type CustomAgentIdentity = z.infer<typeof CustomAgentIdentitySchema>

// Marketplace template classification: DOCUMENT_REVIEW installs a playbook; OTHER is informational.
// Neither fills a workflow block slot.
export const AgentTemplateTypeSchema = z.enum([...TaskAgentTemplateTypeSchema.options, 'DOCUMENT_REVIEW', 'OTHER'])
export type AgentTemplateType = z.infer<typeof AgentTemplateTypeSchema>

export const isTaskAgentTemplateType = (type: AgentTemplateType): type is TaskAgentTemplateType =>
  TaskAgentTemplateTypeSchema.safeParse(type).success

const LegacyAgentIdSchema = z.templateLiteral(['legacy:', z.enum(AGENT_CAPABILITY_KEYS)])
export type LegacyAgentId = z.infer<typeof LegacyAgentIdSchema>

export const BlockAgentTriggerSchema = z.enum(['STEP_START', 'MANUAL'])
export type BlockAgentTrigger = z.infer<typeof BlockAgentTriggerSchema>

const blockAgentShape = {
  id: z.union([z.uuid(), LegacyAgentIdSchema]),
  formTemplateIds: z.array(ref('formTemplate')),
  trigger: BlockAgentTriggerSchema.optional(),
}

const contentGenerationBlockAgent = z.object({
  ...blockAgentShape,
  type: z.literal(TaskAgentTemplateTypeSchema.enum.CONTENT_GENERATION),
  config: AgenticContentGenerationConfigSchema,
})

export const BlockAgentSchema = z
  .discriminatedUnion('type', [
    z.object({
      ...blockAgentShape,
      type: z.literal(TaskAgentTemplateTypeSchema.enum.REVIEW),
      config: AgenticReviewConfigSchema,
    }),
    z.object({
      ...blockAgentShape,
      type: z.literal(TaskAgentTemplateTypeSchema.enum.FILL_IN),
      config: AgenticFillInConfigSchema,
    }),
    contentGenerationBlockAgent,
  ])
  .superRefine((agent, context) => checkSingleConfigSource(agent.config, context, ['config']))
export type BlockAgent = z.infer<typeof BlockAgentSchema>

// For blocks that only compose: a review or fill-in agent would otherwise get a pending run the block has no task for.
export const ContentGenerationBlockAgentSchema = contentGenerationBlockAgent.superRefine((agent, context) =>
  checkSingleConfigSource(agent.config, context, ['config'])
)

// Exhaustive by construction: a new TaskAgentTemplateType fails to compile until it is listed here, so a
// future type breaks the build rather than silently reusing another type's capability key.
export const CAPABILITY_KEY_FOR_TYPE: Record<TaskAgentTemplateType, AgentCapabilityKey> = {
  REVIEW: 'review',
  FILL_IN: 'fillIn',
  CONTENT_GENERATION: 'contentGeneration',
}

// What `payload.agentConfig` can hold on a block config: either shape is valid on read; writers emit V2.
export type AgentConfig = LegacyAgentConfig | AgentConfigV2

export const isAgentConfigV2 = (config: unknown): config is AgentConfigV2 =>
  config != null && typeof config === 'object' && !Array.isArray(config) && AGENT_CAPABILITY_KEYS.some((key) => key in config)

// V2 configs nest each capability under its key. Detected structurally so a raw jsonb blob doesn't need
// to be typed before unwrapping.
const isRawAgentConfigV2 = (raw: unknown): raw is Partial<Record<AgentCapabilityKey, unknown>> =>
  typeof raw === 'object' && raw !== null && AGENT_CAPABILITY_KEYS.some((key) => key in raw)

// Reads one capability off a config, failing safe to undefined on null/non-object/legacy jsonb rather
// than throwing on the `in` probe.
const getAgentCapabilityConfig = <Key extends AgentCapabilityKey>(
  config: AgentConfig | null | undefined,
  key: Key
): AgentConfigV2[Key] => (config != null && typeof config === 'object' && isAgentConfigV2(config) ? config[key] : undefined)

// Reading a legacy flat config folds its top-level policy list into the `slices.policies` entry (a
// present entry is authoritative), so callers always get the V2 review shape — and saving a config read
// through here migrates it. The legacy fallback is review-only: every other capability was born V2.
export const getAgenticReviewConfig = (config: AgentConfig | null | undefined): AgenticReviewConfig | undefined => {
  // Read from unvalidated persisted jsonb: fail safe on null/non-object rather than throw on the `in` probe.
  if (config == null || typeof config !== 'object') return undefined
  if (isAgentConfigV2(config)) return config.review
  // `policyIds` may be stored as null, which a destructuring default would not replace.
  const { policyIds, ...review } = config
  if (review.slices?.policies || !policyIds?.length) return review
  return { ...review, slices: { ...review.slices, policies: { enabled: true, policyIds } } }
}

export const getAgenticFillInConfig = (config: AgentConfig | null | undefined): AgenticFillInConfig | undefined =>
  getAgentCapabilityConfig(config, 'fillIn')

export const getAgenticContentGenerationConfig = (
  config: AgentConfig | null | undefined
): AgenticContentGenerationConfig | undefined => getAgentCapabilityConfig(config, 'contentGeneration')

export const legacyAgentId = (key: AgentCapabilityKey): LegacyAgentId => `legacy:${key}`

export type BlockAgentSource = {
  tasks?: BlockTask[]
  agents?: BlockAgent[] | null
  agentConfig?: AgentConfig | null
}

const formTemplateIdsOfType = (tasks: BlockAgentSource['tasks'], type: TaskAgentTemplateType): string[] =>
  tasks?.flatMap((task) => (task.type === RequestStepTaskTypeEnum[type] && task.formTemplateId ? [task.formTemplateId] : [])) ??
  []

// Stored jsonb may hold a null capability; it is read as absent rather than as an agent with no config.
// TODO(SOUR-1794): drop once stored configs carry `agents` and `agentConfig` is no longer written.
const legacyBlockAgent = (key: AgentCapabilityKey, { tasks, agentConfig }: BlockAgentSource): BlockAgent | undefined => {
  const id = legacyAgentId(key)
  switch (key) {
    case 'review': {
      const config = getAgenticReviewConfig(agentConfig)
      return config == null ? undefined : { id, type: 'REVIEW', formTemplateIds: formTemplateIdsOfType(tasks, 'REVIEW'), config }
    }
    case 'fillIn': {
      const config = getAgenticFillInConfig(agentConfig)
      return config == null
        ? undefined
        : { id, type: 'FILL_IN', formTemplateIds: formTemplateIdsOfType(tasks, 'FILL_IN'), config }
    }
    case 'contentGeneration': {
      const config = getAgenticContentGenerationConfig(agentConfig)
      return config == null
        ? undefined
        : { id, type: 'CONTENT_GENERATION', formTemplateIds: formTemplateIdsOfType(tasks, 'CONTENT_GENERATION'), config }
    }
    default:
      throw new AssertNeverError(key)
  }
}

// A present `agents` list, even an empty one, means the writer has adopted it, so `agentConfig` is ignored.
// A block still on `agentConfig` gets one agent per capability, covering every task of that capability's type.
export const getBlockAgents = (payload: BlockAgentSource): BlockAgent[] => {
  if (Array.isArray(payload.agents)) return payload.agents
  return AGENT_CAPABILITY_KEYS.flatMap((key) => legacyBlockAgent(key, payload) ?? [])
}

// Review and fill-in run one agent per step. A compose reader that cannot name its task gets the block's first.
export const getFirstBlockAgentOfType = <Type extends BlockAgent['type']>(
  payload: BlockAgentSource,
  type: Type
): Extract<BlockAgent, { type: Type }> | undefined =>
  getBlockAgents(payload).find((agent): agent is Extract<BlockAgent, { type: Type }> => agent.type === type)

// Falls back to the first compose agent for a legacy block, whose single agent carries every task.
export const getContentGenerationAgentForFormTemplate = (
  payload: BlockAgentSource,
  formTemplateId: string | null | undefined
): Extract<BlockAgent, { type: 'CONTENT_GENERATION' }> | undefined => {
  const composeAgents = getBlockAgents(payload).filter(
    (agent): agent is Extract<BlockAgent, { type: 'CONTENT_GENERATION' }> => agent.type === 'CONTENT_GENERATION'
  )
  return (
    (formTemplateId == null ? undefined : composeAgents.find((agent) => agent.formTemplateIds.includes(formTemplateId))) ??
    (composeAgents.length === 1 ? composeAgents[0] : undefined)
  )
}

// The capabilities a config switches on. Ask this rather than or-ing the individual getters: a gate that
// names its capabilities goes stale the moment one is added.
export const getEnabledAgentCapabilities = (config: AgentConfig | null | undefined): AgentCapabilityKey[] => {
  if (config == null || typeof config !== 'object') return []
  // A legacy flat config is its own review config, and carries no other capability.
  if (!isAgentConfigV2(config)) return config.enabled === true ? ['review'] : []
  return AGENT_CAPABILITY_KEYS.filter((key) => config[key]?.enabled === true)
}

const disableReviewAutonomyForRollback = (
  autonomy: AgenticReviewConfig['autonomy']
): NonNullable<AgenticReviewConfig['autonomy']> => ({
  approval: { enabled: false },
  rejection: autonomy?.rejection ?? { enabled: false },
  suggestedActions: { enabled: false },
})

// Review keeps its rejection autonomy: that alone doesn't auto-complete the step. Don't refactor to a
// `(key, capability)` parameter — it type-checks against the union, so the branches stop being safe.
const withCapabilityAutonomyDisabled = (config: AgentConfigV2, key: AgentCapabilityKey): AgentConfigV2 => {
  switch (key) {
    case 'review':
      return config.review === undefined
        ? config
        : { ...config, review: { ...config.review, autonomy: disableReviewAutonomyForRollback(config.review.autonomy) } }
    case 'fillIn':
      return config.fillIn === undefined ? config : { ...config, fillIn: { ...config.fillIn, autonomy: { enabled: false } } }
    case 'contentGeneration':
      return config.contentGeneration === undefined
        ? config
        : { ...config, contentGeneration: { ...config.contentGeneration, autonomy: { enabled: false } } }
    default:
      throw new AssertNeverError(key)
  }
}

// Configs saved before this opt-in existed auto-posted whenever approve or reject autonomy was on, so a missing
// value keeps doing that; only an explicit `false` turns it off.
export const isSuggestedActionsAutonomyEnabled = (autonomy: AgenticReviewConfigRead['autonomy']): boolean => {
  const explicit = autonomy?.suggestedActions?.enabled
  if (explicit !== undefined) return explicit
  return autonomy?.approval?.enabled === true || autonomy?.rejection?.enabled === true
}

// Writes the effective value, so a config saved from the UI no longer leans on the legacy fallback above.
export const withExplicitSuggestedActions = (autonomy: AgenticReviewConfig['autonomy']): AgenticReviewConfig['autonomy'] =>
  autonomy === undefined || autonomy.suggestedActions !== undefined
    ? autonomy
    : { ...autonomy, suggestedActions: { enabled: isSuggestedActionsAutonomyEnabled(autonomy) } }

// Reads what withCapabilityAutonomyDisabled writes, so the pre-rollback warning names exactly what
// rollback switches off. The pairing is asserted per capability in the tests, not by the types.
export const hasAutonomyDisabledByRollback = (config: AgentConfig | null | undefined): boolean => {
  // Read from unvalidated persisted jsonb: fail safe on null/non-object rather than throw on the `in` probe.
  if (config == null || typeof config !== 'object') return false
  if (!isAgentConfigV2(config)) {
    return config.autonomy?.approval?.enabled === true || isSuggestedActionsAutonomyEnabled(config.autonomy)
  }
  return (
    config.review?.autonomy?.approval?.enabled === true ||
    isSuggestedActionsAutonomyEnabled(config.review?.autonomy) ||
    config.fillIn?.autonomy?.enabled === true ||
    config.contentGeneration?.autonomy?.enabled === true
  )
}

// Used on rollback: every autonomy that would complete the step or message the requester on the agent's own
// is switched off, so a human must apply the decision and complete the tasks. The agent still recommends and
// still fills forms in.
export const withAutonomyDisabledForRollback = (config: AgentConfig): AgentConfig => {
  if (!isAgentConfigV2(config)) {
    return { ...config, autonomy: disableReviewAutonomyForRollback(config.autonomy) }
  }
  return AGENT_CAPABILITY_KEYS.reduce<AgentConfigV2>(withCapabilityAutonomyDisabled, config)
}

export const AgenticReviewConfigReadSchema = reviewContract.readSchema
export type AgenticReviewConfigRead = z.infer<typeof AgenticReviewConfigReadSchema>

// Accepts both the legacy flat shape and V2; always returns the review config.
export const parseAgenticReviewConfigForRead = (
  raw: unknown
): { config: AgenticReviewConfigRead | undefined; malformed: boolean } =>
  reviewContract.parseForRead(isRawAgentConfigV2(raw) ? raw.review : raw)

export const AgenticFillInConfigReadSchema = fillInContract.readSchema
export type AgenticFillInConfigRead = z.infer<typeof AgenticFillInConfigReadSchema>

// A legacy flat config has no fill-in part, so it parses to `undefined` (never malformed) rather than
// being misread as fill-in config.
export const parseAgenticFillInConfigForRead = (
  raw: unknown
): { config: AgenticFillInConfigRead | undefined; malformed: boolean } =>
  fillInContract.parseForRead(isRawAgentConfigV2(raw) ? raw.fillIn : undefined)

export const AgenticContentGenerationConfigReadSchema = contentGenerationContract.readSchema
export type AgenticContentGenerationConfigRead = z.infer<typeof AgenticContentGenerationConfigReadSchema>

// Born V2 like fill-in: a legacy flat config has no content generation part.
export const parseAgenticContentGenerationConfigForRead = (
  raw: unknown
): { config: AgenticContentGenerationConfigRead | undefined; malformed: boolean } =>
  contentGenerationContract.parseForRead(isRawAgentConfigV2(raw) ? raw.contentGeneration : undefined)

// The cast target for `step.input.metadata.config` in task-agent code: label + agentConfig, the two fields every
// agentic block type is guaranteed to carry. Keep it to universally-present fields — never widen it with block-specific ones.
export interface AgenticBlockConfig {
  payload: {
    label: string
    agentConfig?: AgentConfig
    agents?: BlockAgent[]
  }
}
